/**
 * admin_user_management_controller.js — Admin user management page (Stimulus).
 *
 * Ports the former static/js/admin_user_management.js
 * (attachOnce('adminUserMgmtListeners') + turbo:load) to an element-scoped
 * controller. The page is a client-side searchable/filterable user table with
 * role chip filters, bulk selection/actions, a create-user form, a sort-by-
 * activity toggle, a store-agent modal, and per-user action handlers.
 *
 * Lifecycle (replaces attachOnce + turbo:load):
 *   connect() resets filter state and binds the search/filter, bulk-selection,
 *   role-chip, sort, create-form, and store-agent-modal listeners on the
 *   freshly-rendered DOM. All listeners are element-bound (on elements inside
 *   the swapped <body>), so they die with the <body> on the next Turbo
 *   navigation — disconnect() has nothing extra to remove. connect() fires per
 *   element on each navigation, so listeners always reattach to fresh DOM — no
 *   once-guard needed.
 *
 * The page-action functions (bulkAction, verifyUser, deleteUser,
 * sendPasswordReset, showUserBusinesses, and the delete-flow helpers) stay as
 * module functions and remain exposed on window for the data-action="call-fn"
 * dispatcher. A row's state changes (admin, beta, test, suspend/activate) are
 * not here: they are Turbo forms in templates/admin/_user_row.html whose
 * routes answer a stream replacing that one row.
 *
 * Turbo-safety note: showDeleteUserModal builds a one-off modal element and
 * wires its confirm button. The original used getElementById(...)
 * .addEventListener('click', ..., {once:true}); this port uses .onclick = (which
 * replaces rather than stacks) per CLAUDE.md's modal-button rule, keeping
 * behaviour identical (the modal element is removed on hidden.bs.modal anyway).
 *
 * data-controller="admin-user-management" hosts on the page's container-fluid.
 * Template: templates/admin/user_management.html
 */

import { Controller } from "@hotwired/stimulus";

    var _adminUserActiveRoles = [];

    // ─── Global functions (called via data-action="call-fn") ─────────

    function bulkAction(action) {
        var selected = Array.from(document.querySelectorAll('.user-checkbox:checked')).map(function(cb) { return cb.value; });
        if (selected.length === 0) return;
        if (!confirm('Are you sure you want to ' + action + ' ' + selected.length + ' user(s)?')) return;
        fetch('/api/admin/users/bulk', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: action, user_ids: selected })
        })
        .then(function(r) { return r.json(); })
        .then(function(data) { if (data.success) window.chippiePageCheck.refresh(); else alert('Failed: ' + (data.message || 'Unknown error')); })
        .catch(function() { alert('An error occurred'); });
    }

    function verifyUser(userId) {
        if (!confirm('Mark this user\'s email as verified?')) return;
        fetch('/api/admin/users/' + userId + '/verify', { method: 'POST' })
        .then(function(r) { return r.json(); })
        .then(function(data) { if (data.success) window.chippiePageCheck.refresh(); else alert('Failed: ' + (data.message || 'Unknown error')); });
    }

    // afterUrl: where to go once the user is deleted, for a page that shows
    // only that user (templates/admin/user_detail.html); the list passes none.
    function deleteUser(userId, userName, afterUrl) {
        fetch('/admin/api/users/' + userId + '/owned-businesses', { credentials: 'same-origin' })
        .then(function(r) { return r.json(); })
        .then(function(data) {
            var businesses = data.businesses || [];
            if (businesses.length > 0) {
                showDeleteUserModal(userId, userName, businesses, afterUrl);
            } else {
                if (!confirm('Permanently delete user ' + userName + '? This action cannot be undone.')) return;
                executeDeleteUser(userId, false, afterUrl);
            }
        })
        .catch(function() { alert('Failed to check user businesses'); });
    }

    function showDeleteUserModal(userId, userName, businesses, afterUrl) {
        var existing = document.getElementById('deleteUserModal');
        if (existing) existing.remove();

        var listHtml = businesses.map(function(b) {
            return '<li class="list-group-item d-flex justify-content-between align-items-center">'
                + '<span>' + b.name + (b.is_demo ? ' <span class="badge bg-warning text-dark">Demo</span>' : '') + '</span>'
                + '</li>';
        }).join('');

        var modal = document.createElement('div');
        modal.innerHTML = '<div class="modal fade" id="deleteUserModal" tabindex="-1">'
            + '<div class="modal-dialog"><div class="modal-content">'
            + '<div class="modal-header bg-danger text-white"><h5 class="modal-title">Delete User: ' + userName + '</h5>'
            + '<button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button></div>'
            + '<div class="modal-body">'
            + '<p>This user owns <strong>' + businesses.length + ' business(es)</strong> that must be deleted first:</p>'
            + '<ul class="list-group mb-3">' + listHtml + '</ul>'
            + '<div class="alert alert-danger mb-0"><i class="bi bi-exclamation-triangle me-2"></i>'
            + 'This will permanently delete the user, all listed businesses, and their tenant data. This cannot be undone.</div>'
            + '</div>'
            + '<div class="modal-footer">'
            + '<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>'
            + '<button type="button" class="btn btn-danger" id="confirmDeleteUserBtn">'
            + '<i class="bi bi-trash me-1"></i>Delete User &amp; Businesses</button>'
            + '</div></div></div></div>';

        document.body.appendChild(modal.firstChild);
        var bsModal = new bootstrap.Modal(document.getElementById('deleteUserModal'));
        var modalEl = document.getElementById('deleteUserModal');
        // Modal button: .onclick replaces (does not stack) per CLAUDE.md. The
        // modal element is freshly built each call and removed on close.
        document.getElementById('confirmDeleteUserBtn').onclick = function() {
            this.disabled = true;
            this.textContent = 'Deleting...';
            bsModal.hide();
            executeDeleteUser(userId, true, afterUrl);
        };
        modalEl.addEventListener('hidden.bs.modal', function() {
            modalEl.remove();
        }, { once: true });
        bsModal.show();
    }

    // The route answers a Turbo Stream that removes the row (and toasts), so
    // the table keeps its search, role chips and sort.
    function executeDeleteUser(userId, includeBusinesses, afterUrl) {
        var url = '/admin/api/users/' + userId;
        if (includeBusinesses) url += '?include_businesses=1';
        fetch(url, { method: 'DELETE', headers: { 'Accept': 'text/vnd.turbo-stream.html' }, credentials: 'same-origin' })
        .then(function(r) {
            return r.text().then(function(html) {
                window.Turbo.renderStreamMessage(html);
                if (r.ok && afterUrl) window.Turbo.visit(afterUrl);
            });
        })
        .catch(function() { alert('An error occurred'); });
    }

    function sendPasswordReset(userId) {
        if (!confirm('Send password reset email to this user?')) return;
        fetch('/api/admin/users/' + userId + '/password-reset', { method: 'POST' })
        .then(function(r) { return r.json(); })
        .then(function(data) { if (data.success) alert('Password reset email sent'); else alert('Failed: ' + (data.message || 'Unknown error')); });
    }

    function showUserBusinesses(userId) { window.open('/admin/users/' + userId + '/businesses', '_blank'); }

    // ─── Internal helpers (not called from HTML) ─────────────────────

    function _adminUserFilterUsers() {
        var searchEl = document.getElementById('search');
        if (!searchEl) return;
        var searchTerm = searchEl.value.toLowerCase().trim();
        var statusFilter = (document.getElementById('status') || {}).value || '';
        var verifiedFilter = (document.getElementById('verified') || {}).value || '';

        var rows = document.querySelectorAll('tbody tr[data-user-id]');
        var visibleCount = 0;
        rows.forEach(function(row) {
            var matchesSearch = !searchTerm || (row.dataset.search || '').includes(searchTerm);
            var matchesStatus = !statusFilter || row.dataset.status === statusFilter;
            var matchesVerified = !verifiedFilter || row.dataset.verified === verifiedFilter;
            var matchesRole = true;
            if (_adminUserActiveRoles.length > 0) {
                var userRoles = (row.dataset.role || '').trim().split(/\s+/);
                matchesRole = _adminUserActiveRoles.some(function(r) { return userRoles.indexOf(r) > -1; });
            }
            var visible = matchesSearch && matchesStatus && matchesVerified && matchesRole;
            row.style.display = visible ? '' : 'none';
            if (visible) visibleCount++;
        });
        var countEl = document.getElementById('userCount');
        if (countEl) countEl.textContent = visibleCount;
    }

    function _adminUserUpdateSelectAllBtn() {
        var selectAllBtn = document.querySelector('.role-select-all');
        var allChips = document.querySelectorAll('.role-chip');
        if (!selectAllBtn) return;
        if (_adminUserActiveRoles.length === allChips.length) {
            selectAllBtn.textContent = 'None';
            selectAllBtn.classList.add('active');
        } else {
            selectAllBtn.textContent = 'All';
            selectAllBtn.classList.remove('active');
        }
    }

    // Expose for template dispatcher (data-action="call-fn" in global-actions.js).
    window.bulkAction = bulkAction;
    window.deleteUser = deleteUser;
    window.sendPasswordReset = sendPasswordReset;
    window.showUserBusinesses = showUserBusinesses;
    window.verifyUser = verifyUser;

export default class extends Controller {
        connect() {
            // Reset state
            _adminUserActiveRoles = [];

            // Search/filter
            var searchEl = document.getElementById('search');
            var statusEl = document.getElementById('status');
            var verifiedEl = document.getElementById('verified');
            var searchForm = document.getElementById('searchForm');
            if (!searchEl) return;

            searchEl.addEventListener('input', _adminUserFilterUsers);
            statusEl.addEventListener('change', _adminUserFilterUsers);
            verifiedEl.addEventListener('change', _adminUserFilterUsers);
            searchForm.addEventListener('submit', function(e) { e.preventDefault(); });

            // Bulk selection
            var selectAll = document.getElementById('selectAll');
            var bulkActionsCard = document.getElementById('bulkActionsCard');
            var selectedCountEl = document.getElementById('selectedCount');

            function updateBulkUI() {
                var selected = document.querySelectorAll('.user-checkbox:checked');
                if (bulkActionsCard) {
                    if (selected.length > 0) { bulkActionsCard.classList.remove('d-none'); if (selectedCountEl) selectedCountEl.textContent = selected.length; }
                    else { bulkActionsCard.classList.add('d-none'); }
                }
            }

            if (selectAll) {
                selectAll.addEventListener('change', function() {
                    document.querySelectorAll('.user-checkbox').forEach(function(cb) { cb.checked = selectAll.checked; });
                    updateBulkUI();
                });
            }
            // Delegated: a row a stream replaced brings a new checkbox.
            this.element.addEventListener('change', function(e) {
                if (e.target.classList.contains('user-checkbox')) updateBulkUI();
            });

            // Role chips
            document.querySelectorAll('.role-chip').forEach(function(chip) {
                chip.addEventListener('click', function() {
                    var role = this.dataset.role;
                    var idx = _adminUserActiveRoles.indexOf(role);
                    if (idx > -1) { _adminUserActiveRoles.splice(idx, 1); this.classList.remove('active'); }
                    else { _adminUserActiveRoles.push(role); this.classList.add('active'); }
                    _adminUserUpdateSelectAllBtn();
                    _adminUserFilterUsers();
                });
            });

            var selectAllBtn = document.querySelector('.role-select-all');
            if (selectAllBtn) {
                selectAllBtn.addEventListener('click', function() {
                    var allChips = document.querySelectorAll('.role-chip');
                    if (_adminUserActiveRoles.length === allChips.length) {
                        _adminUserActiveRoles = [];
                        allChips.forEach(function(c) { c.classList.remove('active'); });
                    } else {
                        _adminUserActiveRoles = [];
                        allChips.forEach(function(c) { _adminUserActiveRoles.push(c.dataset.role); c.classList.add('active'); });
                    }
                    _adminUserUpdateSelectAllBtn();
                    _adminUserFilterUsers();
                });
            }

            // Sort by engagement score (application/services/engagement_scoring.py)
            var sortBtn = document.getElementById('sortByActivity');
            if (sortBtn) {
                var sortedDesc = false;
                sortBtn.addEventListener('click', function() {
                    var tbody = document.querySelector('table tbody');
                    if (!tbody) return;
                    var rows = Array.from(tbody.querySelectorAll('tr[data-user-id]'));
                    sortedDesc = !sortedDesc;
                    rows.sort(function(a, b) {
                        var aScore = parseInt(a.dataset.score || '0', 10);
                        var bScore = parseInt(b.dataset.score || '0', 10);
                        return sortedDesc ? bScore - aScore : aScore - bScore;
                    });
                    rows.forEach(function(row) { tbody.appendChild(row); });
                    sortBtn.classList.toggle('active', sortedDesc);
                    sortBtn.innerHTML = sortedDesc
                        ? '<i class="bi bi-sort-down me-1"></i>Most Active'
                        : '<i class="bi bi-sort-up me-1"></i>Least Active';
                });
            }

            // Create user form
            var createForm = document.getElementById('createUserForm');
            if (createForm) {
                createForm.addEventListener('submit', function(e) {
                    e.preventDefault();
                    var formData = new FormData(this);
                    var data = Object.fromEntries(formData);
                    data.is_verified = document.getElementById('isVerified').checked;
                    data.send_welcome_email = document.getElementById('sendWelcomeEmail').checked;
                    var btn = document.getElementById('createUserBtn');
                    var spinner = btn.querySelector('.spinner-border');
                    btn.disabled = true;
                    spinner.classList.remove('d-none');
                    fetch('/api/admin/users', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(data)
                    })
                    .then(function(r) { return r.json(); })
                    .then(function(data) {
                        if (data.success) { bootstrap.Modal.getInstance(document.getElementById('createUserModal')).hide(); window.chippiePageCheck.refresh(); }
                        else { alert('Failed: ' + (data.message || 'Unknown error')); }
                    })
                    .catch(function() { alert('An error occurred'); })
                    .finally(function() { btn.disabled = false; spinner.classList.add('d-none'); });
                });
            }

            // Store agent modal
            var storeModal = document.getElementById('addStoreAgentModal');
            if (storeModal) {
                storeModal.addEventListener('show.bs.modal', function(event) {
                    var trigger = event.relatedTarget;
                    if (!trigger) return;
                    document.getElementById('storeAgentUserName').textContent = trigger.dataset.userName;
                    document.getElementById('addStoreAgentForm').action = '/admin/users/' + trigger.dataset.userId + '/add-store-agent';
                });
            }
        }
}
